import CheckIcon from '@mui/icons-material/Check';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsBrightnessOutlinedIcon from '@mui/icons-material/SettingsBrightnessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { type ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { BrandMark } from '@/components/common/BrandMark';
import { SessionAvatar } from '@/components/common/SessionAvatar';
import { NotificationDrawer } from '@/components/notifications';
import { ROUTES } from '@/constants/routes';
import { useWorkspace } from '@/features/company-portal/workspace/WorkspaceProvider';
import { useUnreadNotificationCount } from '@/features/notifications/hooks/useUnreadNotificationCount';
import { useDashboardTargets } from '@/hooks/useDashboardTarget';
import { useThemeMode } from '@/hooks/useThemeMode';
import type { ThemePreference } from '@/providers/ThemeModeProvider';

export interface TopNavProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
  /**
   * Collapse/expand the desktop side nav. Supplied only by shells that actually have one — the
   * public layout shares this bar and has no nav to collapse.
   */
  onToggleNav?: () => void;
  /** Current state of the desktop side nav, for the toggle's icon and `aria-expanded`. */
  navCollapsed?: boolean;
}

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <LightModeOutlinedIcon fontSize="small" /> },
  { value: 'dark', label: 'Dark', icon: <DarkModeOutlinedIcon fontSize="small" /> },
  { value: 'system', label: 'System', icon: <SettingsBrightnessOutlinedIcon fontSize="small" /> },
];

/** The generic top bar shared by every dashboard-style layout — persona nav lives in `SideNav`. */
export function TopNav({
  onMenuClick,
  showMenuButton = false,
  onToggleNav,
  navCollapsed = false,
}: TopNavProps) {
  const { user, logout } = useAuth();
  const { mode, preference, setPreference, toggleMode } = useThemeMode();
  const workspace = useWorkspace();
  const dashboards = useDashboardTargets();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);
  // The bell badge. Skipped for signed-out shells — the bell only renders when authenticated.
  const { count: unreadCount } = useUnreadNotificationCount(!user);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate(ROUTES.login);
  };

  const closeAndNavigate = (to: string) => {
    setAnchorEl(null);
    navigate(to);
  };

  return (
    <AppBar
      position="fixed"
      color="inherit"
      elevation={0}
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      <Toolbar sx={{ gap: 1 }}>
        {/*
          Two buttons, never both visible: below `md` the side nav is a temporary overlay that can
          only be opened, at `md` and up it is permanent and can only be collapsed. One shared
          button would have to mean different things at different widths.
        */}
        {showMenuButton ? (
          <IconButton
            edge="start"
            onClick={onMenuClick}
            aria-label="Open navigation menu"
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
        ) : null}

        {onToggleNav ? (
          <Tooltip title={navCollapsed ? 'Expand menu' : 'Collapse menu'}>
            <IconButton
              edge="start"
              onClick={onToggleNav}
              aria-label={navCollapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
              aria-expanded={!navCollapsed}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            >
              {navCollapsed ? <MenuIcon /> : <MenuOpenIcon />}
            </IconButton>
          </Tooltip>
        ) : null}

        {/* The brand links home from every shell this bar appears in — the public pages (`/jobs`,
            `/companies`, job and company detail, legal) and the authenticated dashboards alike. `/` is
            correct in both: `RootRoute` gives an anonymous visitor the landing page and hands a
            signed-in one to `RootRedirect`, which resolves their own workspace exactly as it always
            has. The spacer keeps the free width so the anchor is the brand's own box and not the whole
            empty middle of the toolbar. */}
        <Box sx={{ flexGrow: 1 }}>
          <BrandMark logoSize={26} variant="h6" to={ROUTES.root} />
        </Box>

        <Tooltip title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          <IconButton onClick={toggleMode} aria-label="Toggle color mode">
            {mode === 'dark' ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </IconButton>
        </Tooltip>

        {user ? (
          <>
            <Tooltip title="Notifications">
              <IconButton
                aria-label={
                  unreadCount > 0 ? `Notifications (${unreadCount} unread)` : 'Notifications'
                }
                aria-haspopup="dialog"
                aria-expanded={notificationsOpen}
                onClick={() => setNotificationsOpen(true)}
              >
                <Badge badgeContent={unreadCount} color="error" max={99} overlap="circular">
                  <NotificationsNoneIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <NotificationDrawer
              open={notificationsOpen}
              onClose={() => setNotificationsOpen(false)}
            />

            <Tooltip title="Account">
              <IconButton
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-label="Account menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                size="small"
              >
                <SessionAvatar size={36} />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={() => setAnchorEl(null)}
              slotProps={{ paper: { sx: { minWidth: 240 } } }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" noWrap>
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{ display: 'block' }}
                >
                  {user.email}
                </Typography>
              </Box>
              <Divider />

              <MenuItem onClick={() => closeAndNavigate(ROUTES.profile)}>
                <ListItemIcon>
                  <PersonOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>My Profile</ListItemText>
              </MenuItem>
              {/* Workspaces & dashboards: staff dashboards from roles, the Candidate workspace,
                  and one entry PER company membership (labelled with the company name — never a
                  generic "Company Dashboard"). Switching a workspace reuses the same session; a
                  company entry also activates that company before navigating. */}
              {dashboards.map((dashboard) => (
                <MenuItem
                  key={dashboard.companyId ?? dashboard.path}
                  selected={
                    location.pathname.startsWith(dashboard.path) &&
                    (!dashboard.companyId || workspace.activeCompanyId === dashboard.companyId)
                  }
                  onClick={() => {
                    if (dashboard.companyId) {
                      workspace.setActiveCompany(dashboard.companyId);
                    } else if (dashboard.path === ROUTES.candidate) {
                      workspace.rememberWorkspace({ kind: 'candidate', companyId: null });
                    }
                    closeAndNavigate(dashboard.path);
                  }}
                >
                  <ListItemIcon>
                    <DashboardOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{dashboard.label}</ListItemText>
                </MenuItem>
              ))}
              <MenuItem onClick={() => closeAndNavigate(ROUTES.changePassword)}>
                <ListItemIcon>
                  <VpnKeyOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Password</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => closeAndNavigate(ROUTES.mfaSetup)}>
                <ListItemIcon>
                  <SettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Security (MFA)</ListItemText>
              </MenuItem>

              <Divider />
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ px: 2, py: 0.5, display: 'block' }}
              >
                Theme
              </Typography>
              {THEME_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={preference === option.value}
                  onClick={() => setPreference(option.value)}
                >
                  <ListItemIcon>{option.icon}</ListItemIcon>
                  <ListItemText>{option.label}</ListItemText>
                  {preference === option.value ? (
                    <CheckIcon fontSize="small" color="primary" />
                  ) : null}
                </MenuItem>
              ))}

              <Divider />
              {/* Support email, not a "help center coming soon" toast. Points at
                  ragatechsource.com, not willnq.com: willnq.com is send-only (no MX record, so it
                  receives nothing), while ragatechsource.com is where the mailboxes actually are.
                  A mailto: to a domain that cannot accept mail fails silently in the user's client,
                  which is worse than the toast this replaced. */}
              <MenuItem
                component="a"
                href="mailto:contact@ragatechsource.com"
                onClick={() => setAnchorEl(null)}
              >
                <ListItemIcon>
                  <HelpOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Contact support</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Sign out</ListItemText>
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button variant="contained" onClick={() => navigate(ROUTES.login)}>
            Sign in
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
}
